import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CATEGORIES, STORE } from "../lib/constants";

const ENQUIRY_TOPICS = ["General", "Custom Order", ...CATEGORIES];

export default function ContactPage() {
  const [params] = useSearchParams();
  const productName = params.get("product") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: productName ? "General" : "General",
    message: productName ? `Hello! I would love to know more about the "${productName}".` : "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, product_name: productName });
      toast.success("Your enquiry is on its way — we will write back soon!");
      setForm({ name: "", email: "", phone: "", category: "General", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-4 py-2.5 text-sm text-[#292725] placeholder:text-[#8A7E70] focus:border-[#C27803] focus:outline-none focus:ring-2 focus:ring-[#C27803]/30";

  return (
    <div data-testid="contact-page">
      <section className="grain-overlay bg-[#111110] py-16 sm:py-20">
        <div className="crow-container">
          <p className="font-hand text-2xl text-[#C27803]">contact &amp; visit us</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-[#FDFBF7] sm:text-5xl">
            We would love to hear from you
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#E8DFC8]">
            Questions about a piece, a custom order, or a gift for someone dear? Send a note below — or better yet,
            come sit a spell at the shop.
          </p>
        </div>
      </section>

      <section className="crow-container grid gap-12 py-12 sm:py-16 lg:grid-cols-5">
        <form onSubmit={submit} className="rounded-md border border-[#D8CDBC] bg-[#F4EFE6] p-8 shadow-sm lg:col-span-3" data-testid="enquiry-form">
          <h2 className="font-serif text-2xl font-semibold text-[#292725]">Send an Enquiry</h2>
          {productName && (
            <p className="mt-3 rounded-md border border-[#C27803]/40 bg-[#C27803]/10 px-4 py-2 text-sm text-[#9A5E02]" data-testid="enquiry-product-note">
              Asking about: <span className="font-semibold">{productName}</span>
            </p>
          )}
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="enquiry-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Name *</label>
              <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Email *</label>
              <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Phone</label>
              <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-category" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Topic</label>
              <select id="enquiry-category" data-testid="enquiry-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                {ENQUIRY_TOPICS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label htmlFor="enquiry-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Message *</label>
            <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Tell us what you are looking for..." className={inputCls} />
          </div>
          <button
            type="submit"
            data-testid="enquiry-form-submit-button"
            disabled={sending}
            className="mt-6 rounded-md bg-[#C27803] px-8 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02] disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send the Enquiry"}
          </button>
        </form>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-6 shadow-sm" data-testid="store-info-card">
            <h3 className="font-serif text-xl font-semibold text-[#292725]">Find the Shop</h3>
            <ul className="mt-4 space-y-3 text-sm text-[#5C554B]">
              <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C27803]" /> {STORE.address}</li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-[#C27803]" />
                <a href={`tel:${STORE.phone}`} data-testid="contact-phone-link" className="hover:text-[#C27803]">{STORE.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-[#C27803]" />
                <a href={`mailto:${STORE.email}`} data-testid="contact-email-link" className="hover:text-[#C27803]">{STORE.email}</a>
              </li>
            </ul>
            <div className="mt-5 overflow-hidden rounded-md border border-[#D8CDBC]" data-testid="store-map">
              <iframe
                title="Map to The Olde Crow Boutique at Sunnyside Mall"
                src="https://maps.google.com/maps?q=Sunnyside%20Mall%2C%20595%20Bedford%20Hwy%2C%20Bedford%2C%20Nova%20Scotia&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="h-64 w-full"
                loading="lazy"
              />
              <p className="bg-[#F4EFE6] px-3 py-2 text-center font-hand text-lg text-[#292725]">inside Sunnyside Mall — look for the crow on the sign</p>
            </div>
          </div>

          <div className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-6 shadow-sm" data-testid="hours-card">
            <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#292725]">
              <Clock className="h-5 w-5 text-[#C27803]" /> Shop Hours
            </h3>
            <ul className="mt-4 divide-y divide-dashed divide-[#D8CDBC]">
              {STORE.hours.map((h) => (
                <li key={h.days} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="font-medium text-[#292725]">{h.days}</span>
                  <span className={h.time === "Closed" ? "font-hand text-lg text-[#8C251C]" : "text-[#8A7E70]"}>{h.time}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 rounded-md bg-[#8C251C]/5 px-3 py-2 text-xs leading-relaxed text-[#8C251C]">
              Seasonal note: extended hours during the autumn harvest festival and December candlelight evenings.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
